import React, { useRef, useEffect, useState } from 'react';
import { Form, Row, Col, Button, InputNumber, Select, Tabs, Input, Modal, DatePicker, TimePicker, Cascader, message, Popconfirm } from 'antd';
import { EditOutlined, PlusCircleOutlined } from '@ant-design/icons';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import { useAuth } from '../contexts/AuthContext'; // 假设使用了AuthContext

const { Option } = Select;

// 富文本编辑器配置
const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    ['link', 'image'],
    ['clean']
  ],
};

const quillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet',
  'link', 'image'
];

const PostClassFeedback = ({
  currentScheduleMember,
  totalRecord,
  completeClass,
  externalFinishConfirmVisible,
  setExternalFinishConfirmVisible,
  loading,
  scheduleModalVisible,
  setScheduleModalVisible,
  summaryViewMode,
  setSummaryViewMode,
  suggestionViewMode,
  setSuggestionViewMode,
  normalizeRichText,
  scheduleForm,
  summaryRef,
  suggestionRef,
  handleCopyPostClassHistory,
  sportsOptions,
  visibleTabs = ['1', '2'],
  handleDateChange,
  scheduleRef,
  prescriptionId
}) => {
  // 设置 dayjs 为中文
  dayjs.locale('zh-cn');
  const [localLoading, setLocalLoading] = useState(false);
  const [localTotalRecord, setLocalTotalRecord] = useState(0);
  const [localCompleteClass, setLocalCompleteClass] = useState('');
  const [localPrescriptionId, setLocalPrescriptionId] = useState(null);
  const [localSportsOptions, setLocalSportsOptions] = useState([]);
  const [finishConfirmVisible, setFinishConfirmVisible] = useState(false);
  // 使用本地状态管理编辑/查看模式
  const [localSummaryViewMode, setLocalSummaryViewMode] = useState(true);
  const [localSuggestionViewMode, setLocalSuggestionViewMode] = useState(true);
  const { apiClient } = useAuth(); // 使用apiClient

  // HTML解码函数
  const decodeHtml = (html) => {
    const txt = document.createElement('textarea');
    txt.innerHTML = html;
    return txt.value;
  };

  // 将纯文本或旧格式统一转成安全的 HTML，用于展示模式
  const normalizeRichTextLocal = (raw) => {
    if (!raw) return '';
    let content = String(raw);
    // 先进行 HTML 解码，兼容后端存的转义内容
    try {
      content = decodeHtml(content);
    } catch (e) {
      // ignore
    }
    const trimmed = content.trim();
    if (!trimmed) return '';

    // 简单判断是否已经是 HTML 结构（包括 Quill 生成的内容）
    const htmlLikePattern = /<([a-zA-Z][^>]*)>/;
    if (
      htmlLikePattern.test(trimmed) ||
      /&nbsp;/.test(trimmed) ||
      trimmed.startsWith('<p') ||
      trimmed.startsWith('<div')
    ) {
      return trimmed;
    }

    // 纯文本：需要做转义并按换行转成 <p>/<br>
    const escaped = trimmed
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    const lines = escaped.split(/\r?\n/);
    if (lines.length === 1) {
      return `<p>${lines[0] || ''}</p>`;
    }
    return lines
      .map((line) => `<p>${line || ''}</p>`)
      .join('');
  };

  // 课前/课后排课表单的初始值（用于弹框打开或切换日期无数据时重置）
  const buildInitialScheduleValues = (override = {}) => {
    const today = dayjs();
    const defaultTime = dayjs().hour(16).minute(0).second(0);
    return {
      trainDate: override.trainDate || today,
      trainTime: override.trainTime || defaultTime,
      trainTimes: 1,
      details: [
        {
          sports: [],
          sportsContent: '',
        },
      ],
      teachingMode: '',
      summary: '',
      suggestion: '',
      accClasses: '',
      completion: '',
      heartRate: '',
      restTimes: '',
      points: '',
      ...override,
    };
  };

  // 从后端获取排课数据
  useEffect(() => {
    const fetchScheduleData = async () => {
      if (!scheduleModalVisible || !currentScheduleMember || !apiClient) return;

      // 重置所有状态
      setLocalPrescriptionId(null);
      setLocalCompleteClass('');
      setLocalTotalRecord(0);
      setLocalLoading(true);
      setLocalSummaryViewMode(true);
      setLocalSuggestionViewMode(true);
      scheduleForm.setFieldsValue(buildInitialScheduleValues());

      try {
        const today = dayjs();
        const defaultTime = dayjs().hour(16).minute(0).second(0);

        // 如果有传入的ID，优先根据ID查询特定的训练记录
        if (prescriptionId) {
          // 根据ID查询特定的训练记录
          const resp = await apiClient.post('/sports-server-prod/taller.api.v1/prescription/pageList', {
            id: prescriptionId,
            pageSize: 1,
            pageNo: 1,
          });

          if (resp.data && resp.data.code === 200) {
            const list = resp.data.data?.list || [];
            const total = resp.data.data?.totalRecord || 0;
            setLocalTotalRecord(total);
            if (list.length > 0) {
              const p = list[0];
              setLocalPrescriptionId(p.id);
              const trainDateTime = dayjs(p.trainDate || today.format('YYYY-MM-DD 16:00'));
              scheduleForm.setFieldsValue({
                trainDate: trainDateTime.startOf('day'),
                trainTime: trainDateTime,
                trainTimes: p.trainTimes || 1,
                details: (p.details || []).map((d) => {
                  const parts = (d.sportsItem || '').split('/').filter(Boolean);
                  return {
                    sports: parts,
                    sportsContent: d.sportsContent || '',
                  };
                }),
                teachingMode: p.teachingMode || '',
                summary: normalizeRichText(p.briefSummary || ''),
                suggestion: normalizeRichText(p.suggestion || ''),
                // new feedback-related fields
                accClasses: p.accClasses != null ? p.accClasses : undefined,
                completion: p.completion != null ? p.completion : undefined,
                heartRate: p.heartRate != null ? p.heartRate : undefined,
                restTimes: p.restTimes != null ? p.restTimes : undefined,
                points: p.points != null ? p.points : undefined,
              });
              setLocalCompleteClass(p.completeClass || '');
            } else {
              message.error('未找到对应的训练记录');
            }
          } else if (resp.data) {
            message.error(resp.data.msg || '获取排课信息失败');
          }
        } else {
          // 如果没有传入ID，按照原来的方式查询当天的排课信息
          const resp = await apiClient.post('/sports-server-prod/taller.api.v1/prescription/pageList', {
            babyId: currentScheduleMember.id,
            searchDate: today.format('YYYY-MM-DD'),
            pageSize: 1,
            pageNo: 1,
          });

          if (resp.data && resp.data.code === 200) {
            const list = resp.data.data?.list || [];
            const total = resp.data.data?.totalRecord || 0;
            setLocalTotalRecord(total);
            if (list.length > 0) {
              const p = list[0];
              setLocalPrescriptionId(p.id);
              const trainDateTime = dayjs(p.trainDate || today.format('YYYY-MM-DD 16:00'));
              scheduleForm.setFieldsValue({
                trainDate: trainDateTime.startOf('day'),
                trainTime: trainDateTime,
                trainTimes: p.trainTimes || 1,
                details: (p.details || []).map((d) => {
                  const parts = (d.sportsItem || '').split('/').filter(Boolean);
                  return {
                    sports: parts,
                    sportsContent: d.sportsContent || '',
                  };
                }),
                teachingMode: p.teachingMode || '',
                summary: normalizeRichText(p.briefSummary || ''),
                suggestion: normalizeRichText(p.suggestion || ''),
                // new feedback-related fields
                accClasses: p.accClasses != null ? p.accClasses : undefined,
                completion: p.completion != null ? p.completion : undefined,
                heartRate: p.heartRate != null ? p.heartRate : undefined,
                restTimes: p.restTimes != null ? p.restTimes : undefined,
                points: p.points != null ? p.points : undefined,
              });
              setLocalCompleteClass(p.completeClass || '');
            } else {
              // code=200 且列表为空时，保持一个"干净"的初始页面
              setLocalPrescriptionId(null);
              setLocalCompleteClass('');
              setLocalTotalRecord(0);
              setLocalSummaryViewMode(true);
              setLocalSuggestionViewMode(true);
              scheduleForm.setFieldsValue(buildInitialScheduleValues({ trainDate: today, trainTime: defaultTime }));
            }
          } else if (resp.data) {
            message.error(resp.data.msg || '获取排课信息失败');
          }
        }
      } catch (e) {
        console.error('获取排课信息失败:', e);
        message.error('获取排课信息失败');
      } finally {
        setLocalLoading(false);
      }
    };

    fetchScheduleData();
  }, [scheduleModalVisible, currentScheduleMember, normalizeRichText, scheduleForm, setSummaryViewMode, setSuggestionViewMode, apiClient, prescriptionId]);

  // 初始化运动项目枚举
  useEffect(() => {
    if (!apiClient || localSportsOptions.length > 0) return;

    const fetchSportsItems = async () => {
      try {
        const resp = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/list', { tableName: 'SPORTS_ITEM' });
        if (resp.data && resp.data.code === 200 && resp.data.data) {
          let raw = resp.data.data;
          if (typeof raw === 'string') {
            try {
              raw = JSON.parse(raw);
            } catch (e) {
              console.error('解析运动项目枚举失败:', e);
              message.error('解析运动项目配置失败');
              return;
            }
          }
          const options = (raw || []).map((p) => ({
            label: p.enumName,
            value: p.enumName,
            children: (p.items || []).map((c) => ({
              label: c.enumName,
              value: c.enumName,
            })),
          }));
          setLocalSportsOptions(options);
        } else {
          message.error(resp.data?.msg || '获取运动项目失败');
        }
      } catch (e) {
        console.error('获取运动项目失败:', e);
        message.error('获取运动项目失败');
      }
    };

    fetchSportsItems();
  }, [apiClient, localSportsOptions.length]);

  // 处理日期变化，重新获取排课信息
  const handleLocalDateChange = async (date) => {
    if (!date || !currentScheduleMember || !apiClient) return;

    try {
      setLocalLoading(true);
      const resp = await apiClient.post('/sports-server-prod/taller.api.v1/prescription/pageList', {
        babyId: currentScheduleMember.id,
        searchDate: date.format('YYYY-MM-DD'),
        pageSize: 1,
        pageNo: 1,
      });

      if (resp.data && resp.data.code === 200) {
        const list = resp.data.data?.list || [];
        const total = resp.data.data?.totalRecord || 0;
        setLocalTotalRecord(total);
        if (list.length > 0) {
          const p = list[0];
          setLocalPrescriptionId(p.id);
          const trainDateTime = dayjs(p.trainDate || date.format('YYYY-MM-DD 16:00'));
          scheduleForm.setFieldsValue({
            trainTime: trainDateTime,
            trainTimes: p.trainTimes || 1,
            details: (p.details || []).map((d) => {
              const parts = (d.sportsItem || '').split('/').filter(Boolean);
              return {
                sports: parts,
                sportsContent: d.sportsContent || '',
              };
            }),
            teachingMode: p.teachingMode || '',
            summary: normalizeRichText(p.briefSummary || ''),
            suggestion: normalizeRichText(p.suggestion || ''),
            // new feedback-related fields
            accClasses: p.accClasses != null ? p.accClasses : undefined,
            completion: p.completion != null ? p.completion : undefined,
            heartRate: p.heartRate != null ? p.heartRate : undefined,
            restTimes: p.restTimes != null ? p.restTimes : undefined,
            points: p.points != null ? p.points : undefined,
          });
          setLocalCompleteClass(p.completeClass || '');
          setLocalSummaryViewMode(true);
          setLocalSuggestionViewMode(true);
        } else {
          // code=200 但列表为空：保持为"干净"的初始页面，不带上一次的值
          setLocalPrescriptionId(null);
          setLocalCompleteClass('');
          setLocalTotalRecord(0);
          setLocalSummaryViewMode(true);
          setLocalSuggestionViewMode(true);
          const defaultTime = dayjs().hour(16).minute(0).second(0);
          scheduleForm.setFieldsValue(buildInitialScheduleValues({ trainDate: date, trainTime: defaultTime }));
        }
      } else if (resp.data) {
        message.error(resp.data.msg || '获取排课信息失败');
      }
    } catch (e) {
      console.error('获取排课信息失败:', e);
      message.error('获取排课信息失败');
    } finally {
      setLocalLoading(false);
    }
  };

  const handleSaveSchedule = async () => {
    try {
      const values = await scheduleForm.validateFields();
      const userStr = localStorage.getItem('user') || '{}';
      const user = JSON.parse(userStr);

      if (!currentScheduleMember) {
        message.error('未找到当前会员信息');
        return;
      }

      // 校验授课模式是否为必填
      if (!values.teachingMode) {
        message.error('授课模式为必填，请维护会员信息');
        return;
      }

      const trainDate = values.trainDate;
      const trainTime = values.trainTime;

      const combinedDateTime = dayjs(trainDate)
        .hour(trainTime.hour())
        .minute(trainTime.minute())
        .second(0);

      const details = (values.details || [])
        .filter((d) => d && d.sports && d.sports.length > 0)
        .map((d) => ({
          sportsItem: d.sports.join('/'),
          sportsContent: d.sportsContent || '',
        }));

      if (details.length === 0) {
        message.warning('请至少添加一条课前备课记录');
        return;
      }

      // 获取用户存储的信息作为备选值
      let storedUser = null;
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          storedUser = JSON.parse(userStr);
        }
      } catch (e) {
        console.error('解析本地存储的用户信息失败:', e);
      }
      
      const customerId = currentScheduleMember.customerId || (storedUser && storedUser.customerId) || user.customerId || '';
      const classId = currentScheduleMember.classId || (storedUser && storedUser.classId) || '';
      
      const payload = {
        accClasses: values.accClasses || 0,
        babyId: currentScheduleMember.id,
        babyName: currentScheduleMember.babyName,
        briefSummary: values.summary || '',
        coach: `${customerId}-${classId}`,
        completion: values.completion || '',
        createUser: user.name || '',
        customerName: currentScheduleMember.customerName || '',
        details,
        heartRate: values.heartRate || '',
        id: localPrescriptionId || null,
        points: values.points || '',
        restTimes: values.restTimes || '',
        suggestion: values.suggestion || '',
        trainDate: combinedDateTime.format('YYYY-MM-DD HH:mm'),
        trainTimes: values.trainTimes || 1,
        updateUser: user.name || '',
        teachingMode: values.teachingMode || '',
      };

      setLocalLoading(true);
      const resp = await apiClient.post('/sports-server-prod/taller.api.v1/prescription/add', payload);
      if (resp.data && resp.data.code === 200) {
        message.success('排课保存成功');
        setScheduleModalVisible(false);
      } else {
        message.error(resp.data?.msg || '排课保存失败');
      }
    } catch (e) {
      if (e.errorFields) {
        return;
      }
      console.error('保存排课失败:', e);
      message.error('保存排课失败，请稍后重试');
    } finally {
      setLocalLoading(false);
    }
  };

  const exportPDF = async () => {
    if (scheduleRef && scheduleRef.current) {
      try {
        // 临时显示所有Tab内容以便截图
        const tabPanes = scheduleRef.current.querySelectorAll('.ant-tabs-tabpane');
        tabPanes.forEach(pane => {
          pane.style.display = 'block';
          pane.style.visibility = 'visible';
        });
        const canvas = await html2canvas(scheduleRef.current, { scale: 2, useCORS: true });
        // 恢复隐藏
        tabPanes.forEach(pane => {
          pane.style.display = '';
          pane.style.visibility = '';
        });
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', 'a4');
        let imgWidth = 190; // A4 width minus margins
        const pageHeight = 277; // A4 height minus margins
        let imgHeight = (canvas.height * imgWidth) / canvas.width;

        // 如果图像高度超过页面高度，则缩放以适应一页
        if (imgHeight > pageHeight) {
          const scale = pageHeight / imgHeight;
          imgHeight = pageHeight;
          imgWidth *= scale;
        }

        pdf.setFontSize(14);
        const values = scheduleForm.getFieldsValue();
        const dateStr = values.trainDate ? dayjs(values.trainDate).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');
        const name = currentScheduleMember ? currentScheduleMember.babyName : '未知';
        const filename = `${dateStr}${name}`;

        pdf.addImage(imgData, 'PNG', 10, 30, imgWidth, imgHeight);
        
        pdf.save(`${filename}.pdf`);
      } catch (error) {
        console.error('截图失败:', error);
        message.error('导出PDF失败');
      }
    } else {
      message.error('无法导出PDF：组件未正确初始化');
    }
  };

  // 结课处理
  const handleFinishClass = async () => {
    if (!localPrescriptionId) return;

    try {
      setLocalLoading(true);
      const userStr = localStorage.getItem('user') || '{}';
      const user = JSON.parse(userStr);
      const values = scheduleForm.getFieldsValue();

      // 使用 getFieldValue 获取最新值，确保与弹框显示一致
      const trainTimesValue = scheduleForm.getFieldValue('trainTimes');

      // 校验授课模式是否为必填
      if (!values.teachingMode) {
        message.error('授课模式为必填，请维护会员信息');
        setLocalLoading(false);
        return;
      }

      const classTimes = -(trainTimesValue != null ? trainTimesValue : 1);
      // 获取用户存储的信息作为备选值
      let storedUser = null;
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          storedUser = JSON.parse(userStr);
        }
      } catch (e) {
        console.error('解析本地存储的用户信息失败:', e);
      }
      
      const resp = await apiClient.post('/sports-base-server-prod/myPage/updateClassTimes', {
        customerId: currentScheduleMember.customerId || (storedUser && storedUser.customerId),
        classId: currentScheduleMember.classId || (storedUser && storedUser.classId),
        id: [currentScheduleMember.id],
        classTimes: classTimes,
        userId: user.userId
      });
      if (resp && resp.data && (resp.data.result === '1' || resp.data.code === 200)) {
        // 条件满足，不提示信息，再调用真正的结课接口
        const trainDate = values.trainDate;
        const trainTime = values.trainTime;
        const combinedDateTime = dayjs(trainDate).hour(trainTime.hour()).minute(trainTime.minute()).second(0);
        const trainDateStr = combinedDateTime.format('YYYY-MM-DD HH:mm');
        // 获取用户存储的信息作为备选值
        let storedUser = null;
        try {
          const userStr = localStorage.getItem('user');
          if (userStr) {
            storedUser = JSON.parse(userStr);
          }
        } catch (e) {
          console.error('解析本地存储的用户信息失败:', e);
        }
        
        const customerId = currentScheduleMember.customerId || (storedUser && storedUser.customerId);
        const classId = currentScheduleMember.classId || (storedUser && storedUser.classId);
        
        const payload = {
          babyId: currentScheduleMember.id,
          babyName: currentScheduleMember.babyName,
          coach: `${customerId}-${classId}`,
          completeClass: "1",
          consumeClass: trainTimesValue != null ? trainTimesValue : 1,
          createUser: user.name,
          id: localPrescriptionId,
          points: values.points || 0,
          trainDate: trainDateStr,
          updateUser: user.name
        };
        const finishResp = await apiClient.post('/sports-server-prod/taller.api.v1/prescription/add', payload);
        if (finishResp.data && finishResp.data.code === 200) {
          message.success('结课成功');
          setLocalCompleteClass('1');
          setFinishConfirmVisible(false);
          // 同步外部状态（如果存在）
          if (setExternalFinishConfirmVisible) {
            setExternalFinishConfirmVisible(false);
          }
          setScheduleModalVisible(false);
        } else {
          message.error(finishResp.data?.msg || '结课失败');
        }
      } else {
        message.error(resp.data?.msg || '结课失败');
      }
    } catch (e) {
      console.error('结课失败:', e);
      message.error('结课失败，请稍后重试');
    } finally {
      setLocalLoading(false);
    }
  };

  return (
    <>
    <Modal
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', paddingRight: '20px' }}>
          <span style={{ flex: 1 }}>排课</span>
          <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
            <Button
              type="primary"
              size="small"
              onClick={exportPDF}
            >
              导出PDF
            </Button>
            <Popconfirm
              title="确认保存排课信息?"
              description="您确定要保存当前排课信息吗？"
              okText="确定"
              cancelText="取消"
              placement="bottom"
              onConfirm={handleSaveSchedule}
            >
              <Button type="primary" size="small" loading={loading}>
                保存
              </Button>
            </Popconfirm>
          </div>
        </div>
      }
      open={scheduleModalVisible}
      onCancel={() => setScheduleModalVisible(false)}
      closable={true}
      width={1000}
      footer={null}
    >
      <div ref={scheduleRef} style={{ padding: '20px' }}>
        <Form
          form={scheduleForm}
          layout="vertical"
          initialValues={{
            trainTimes: 1,
            details: [{ sports: [], sportsContent: '' }],
            teachingMode: '',
            summary: '',
            suggestion: '',
            accClasses: '',
            completion: '',
            heartRate: '',
            restTimes: '',
            points: '',
          }}
        >
          <Tabs defaultActiveKey="1" forceRender={true} size="small" tabBarStyle={{ marginBottom: 16 }}>
            {visibleTabs.includes('1') && (
              <Tabs.TabPane tab="课前备课" key="1">
                <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                  <Col>
                    <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#1890ff' }}>{currentScheduleMember ? currentScheduleMember.babyName : ''}</span>
                  </Col>
                  <Col>
                    <div style={{ textAlign: 'right' }}>
                      {/* 保存按钮已移至Modal标题栏 */}
                    </div>
                  </Col>
                </Row>
                <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                  <Col span={8}>
                    <Form.Item
                        label="上课日期"
                        name="trainDate"
                        rules={[{ required: true, message: '请选择上课日期' }]}
                    >
                      <DatePicker style={{ width: '100%' }} onChange={handleLocalDateChange}  />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                        label="上课时间"
                        name="trainTime"
                        rules={[{ required: true, message: '请选择上课时间' }]}
                    >
                      <TimePicker style={{ width: '100%' }} format="HH:mm" disabled={localCompleteClass === '1'} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                        label="本节耗课"
                        name="trainTimes"
                        rules={[{ required: true, message: '请输入本节耗课' }]}
                    >
                      <InputNumber min={0} step={1} style={{ width: '100%' }} parser={(value) => value.replace(/\D/g, '')} disabled={localCompleteClass === '1'} />
                    </Form.Item>
                  </Col>
                </Row>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: 8,
                    justifyContent: 'space-between',
                  }}
                >
                  <Button
                    type="dashed"
                    icon={<PlusCircleOutlined />}
                    onClick={() => {
                      const list = scheduleForm.getFieldValue('details') || [];
                      scheduleForm.setFieldsValue({
                        details: [...list, { sports: [], sportsContent: '' }],
                      });
                    }}
                    disabled={localCompleteClass === '1'}
                  >
                    新增一行
                  </Button>
                </div>

                <Form.List name="details">
                  {(fields, { remove }) => (
                    <>
                      {fields.map((field) => (
                        <Row
                          key={field.key}
                          gutter={8}
                          align="middle"
                          style={{ marginBottom: 8 }}

                        >
                          <Col span={1}>
                            <Button
                              danger
                              type="text"
                              onClick={() => remove(field.name)}
                              disabled={localCompleteClass === '1'}
                            >
                              ×
                            </Button>
                          </Col>
                          <Col span={10}>
                            <Form.Item
                              {...field}
                              name={[field.name, 'sports']}
                              fieldKey={[field.fieldKey, 'sports']}
                              rules={[{ required: true, message: '请选择运动项目' }]}
                              disabled={localCompleteClass === '1'}
                            >
                              <Cascader
                                options={localSportsOptions.length > 0 ? localSportsOptions : sportsOptions}
                                placeholder="请选择运动项目"
                                showSearch
                                expandTrigger="hover"
                                disabled={localCompleteClass === '1'}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={13}>
                            <Form.Item
                              {...field}
                              name={[field.name, 'sportsContent']}
                              fieldKey={[field.fieldKey, 'sportsContent']}
                            >
                              <Input disabled={localCompleteClass === '1'} placeholder="请输入训练内容" />
                            </Form.Item>
                          </Col>
                        </Row>
                      ))}
                    </>
                  )}
                </Form.List>
              </Tabs.TabPane>
            )}
            {visibleTabs.includes('2') && (
              <Tabs.TabPane tab="课后反馈" key="2">
                <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                  <Col>
                    <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#1890ff' }}>{currentScheduleMember ? currentScheduleMember.babyName : ''}</span>
                  </Col>
                  <Col>
                    <div style={{ textAlign: 'right' }}>
                      {localTotalRecord > 0 && localCompleteClass !== '1' && (
                        <Button style={{ marginRight: 8 }} type="primary" onClick={() => {
                          setFinishConfirmVisible(true);
                          // 同步外部状态（如果存在）
                          if (setExternalFinishConfirmVisible) {
                            setExternalFinishConfirmVisible(true);
                          }
                        }}>
                          结课
                        </Button>
                      )}
                      {/* 保存按钮已移至Modal标题栏 */}
                    </div>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      label="累计课时"
                      name="accClasses"
                    >
                      <InputNumber min={0} step={1} style={{ width: '100%' }} parser={(value) => value.replace(/\D/g, '')} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      label="完成度"
                      name="completion"
                    >
                      <InputNumber min={0} style={{ width: '100%' }} parser={(value) => value.replace(/\D/g, '')} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      label="心率"
                      name="heartRate"
                    >
                      <InputNumber min={0} style={{ width: '100%' }} parser={(value) => value.replace(/\D/g, '')} />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      label="休息次数"
                      name="restTimes"
                    >
                      <InputNumber min={0} step={1} style={{ width: '100%' }} parser={(value) => value.replace(/\D/g, '')} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      label="奖励积分"
                      name="points"
                    >
                      <InputNumber min={0} step={1} style={{ width: '100%' }} parser={(value) => value.replace(/\D/g, '')} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      label="授课模式"
                      name="teachingMode"

                      rules={[{ required: true, message: '授课模式为必填，请维护会员信息' }]}
                    >
                      <Select placeholder="请选择授课模式" disabled>
                        <Option value=""></Option>
                        <Option value="SVIP专属">SVIP专属</Option>
                        <Option value="VIP私教">VIP私教</Option>
                        <Option value="私教小团">私教小团</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item
                      label={
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>小结</span>
                          <Button
                            type="link"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => setLocalSummaryViewMode((prev) => !prev)}
                          >
                            {localSummaryViewMode ? '编辑' : '查看'}
                          </Button>
                        </div>
                      }
                      name="summary"
                    >
                      {localSummaryViewMode ? (
                        <div
                          style={{
                            minHeight: 80,
                            padding: '8px 12px',
                            border: '1px solid #f0f0f0',
                            borderRadius: 4,
                            background: '#fafafa',
                            whiteSpace: 'normal',
                            wordBreak: 'break-all',
                          }}
                          dangerouslySetInnerHTML={{
                            __html: normalizeRichText(scheduleForm.getFieldValue('summary') || ''),
                          }}
                        />
                      ) : (
                        <ReactQuill
                          ref={summaryRef}
                          theme="snow"
                          modules={quillModules}
                          formats={quillFormats}
                          value={scheduleForm.getFieldValue('summary') || ''}
                          onChange={(content) => {
                            scheduleForm.setFieldsValue({ summary: content || '' });
                          }}
                        />
                      )}
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item
                      label={
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>建议</span>
                          <Button
                            type="link"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => setLocalSuggestionViewMode((prev) => !prev)}
                          >
                            {localSuggestionViewMode ? '编辑' : '查看'}
                          </Button>
                        </div>
                      }
                      name="suggestion"
                    >
                      {localSuggestionViewMode ? (
                        <div
                          style={{
                            minHeight: 80,
                            padding: '8px 12px',
                            border: '1px solid #f0f0f0',
                            borderRadius: 4,
                            background: '#fafafa',
                            whiteSpace: 'normal',
                            wordBreak: 'break-all',
                          }}
                          dangerouslySetInnerHTML={{
                            __html: normalizeRichText(scheduleForm.getFieldValue('suggestion') || ''),
                          }}
                        />
                      ) : (
                        <ReactQuill
                          ref={suggestionRef}
                          theme="snow"
                          modules={quillModules}
                          formats={quillFormats}
                          value={scheduleForm.getFieldValue('suggestion') || ''}
                          onChange={(content) => {
                            scheduleForm.setFieldsValue({ suggestion: content || '' });
                          }}
                        />
                      )}
                    </Form.Item>
                  </Col>
                </Row>
              </Tabs.TabPane>
            )}
          </Tabs>
        </Form>
      </div>
    </Modal>
    {/* 结课确认弹窗 */}
    <Modal
      title="确认结课"
      open={finishConfirmVisible}
      onCancel={() => {
        setFinishConfirmVisible(false);
        // 同步外部状态（如果存在）
        if (setExternalFinishConfirmVisible) {
          setExternalFinishConfirmVisible(false);
        }
      }}
      footer={[
        <Button key="cancel" onClick={() => {
          setFinishConfirmVisible(false);
          // 同步外部状态（如果存在）
          if (setExternalFinishConfirmVisible) {
            setExternalFinishConfirmVisible(false);
          }
        }}>
          取消
        </Button>,
        <Button key="confirm" type="primary" loading={localLoading} onClick={handleFinishClass}>
          确认结课
        </Button>,
      ]}
    >
      <div style={{ padding: '20px' }}>
        <p>确认要结课吗？</p>
        <p><strong>扣课次数：</strong>{scheduleForm.getFieldValue('trainTimes') || 0}</p>
        <p><strong>奖励积分：</strong>{scheduleForm.getFieldValue('points') || 0}</p>
      </div>
    </Modal>
    </>
  );
};

export default PostClassFeedback;
